import assertStatus from "@mittwald/api-client/dist/types/assertStatus";
import * as R from "remeda";
import type { MittwaldApi } from "../../api/Mittwald";
import { mittwaldApi } from "../../api/Mittwald";
import { Money } from "../misc/Money";
import Contributor from "./Contributor";

export type ExtensionApiData =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_Extension;

export type MarketplaceContext =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_Context;

export type MarketplaceExternalComponent =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_ExternalComponent;

export type ExtensionCheckoutRequestBody =
  MittwaldApi.Paths.V2_Extensions_ExtensionId_Checkout.Post.Parameters.RequestBody;

export type ExtensionPricing =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_Marketplace_MonthlyPricingStrategy;

export interface FrontendFragment {
  additionalProperties?: {
    icon?: string;
    title?: string | Record<string, string>;
  };
  url: string;
}

export type FrontendFragments = Record<string, FrontendFragment>;

const staticPrices = [
  // Postmaster
  {
    id: "018fba26-2791-70f7-953c-6becc21f6df5",
    price: 2500,
  },
  // Passwortschutz
  {
    id: "ede29562-9a5b-4bee-81c3-479e878bf259",
    price: 149,
  },
  // PHP-Einstellungen
  {
    id: "ddaeea02-a622-4a51-ba2e-e1a102c3dfab",
    price: 149,
  },
  // Bagel
  {
    id: "e90dcbd5-4c02-415b-9942-395d255bf00d",
    price: 1000,
  },
  // Treesize
  {
    id: "e2fff19a-e58c-4231-b6a4-ef6886ed6f39",
    price: 149,
  },
  // To Do Liste
  {
    id: "e6c56a47-c4dd-474f-b378-48fe2c930b18",
    price: 500,
  },
  // GitLab Deploy
  {
    id: "55438185-001c-41ab-95b5-ca48828f93cd",
    price: 3900,
  },
  // Smart Extension Builder
  {
    id: "3e19a489-86a6-47a0-8b81-5b07cc2e99ee",
    price: 9900,
  },
  // Integration für Cloudflare
  {
    id: "c538a7e5-880d-4e56-b701-66961d75f85a",
    price: 149,
  },
];

class Extension {
  public readonly data: ExtensionApiData;
  public readonly id: string;
  public readonly name: string;
  public readonly isDisabled: boolean;
  public readonly isBlocked: boolean;
  public readonly price?: ReturnType<typeof Money>;
  public readonly pricing?: ReturnType<typeof Money>;
  public readonly context: "project" | "customer";
  public readonly scopes: string[];
  public readonly subTitle: string;
  public readonly detailedDescriptionMarkdown?: string;
  public readonly frontendFragments: FrontendFragments;
  public readonly externalFrontends: MarketplaceExternalComponent[];

  public constructor(data: ExtensionApiData) {
    this.data = Object.freeze(data);
    this.id = data.id;
    this.name = data.name;
    this.isDisabled = data.state === "disabled";
    this.isBlocked = data.state === "blocked";

    const staticPrice = staticPrices.find((p) => p.id === this.id);
    this.price = staticPrice
      ? Money({ amount: staticPrice.price, currency: "EUR" })
      : undefined;
    this.context = data.context;
    this.scopes = data.scopes;
    this.subTitle = data.subTitle.de;
    if (data.pricing?.netPrice) {
      this.pricing = Money({ amount: data.pricing.netPrice, currency: "EUR" });
    }
    this.detailedDescriptionMarkdown = data.detailedDescriptions?.de.markdown;
    this.frontendFragments = this.normalizeFrontendFragments(
      data.frontendFragments,
    );

    this.externalFrontends = data.externalFrontends ?? [];
    if (
      this.externalFrontends.length === 0 &&
      (data.frontendComponents ?? []).length >= 1
    ) {
      this.externalFrontends = data.frontendComponents ?? [];
    }
  }

  public static useLoadById(extensionId: string): Extension {
    const data = mittwaldApi.extensionGetExtension
      .getResource({ path: { extensionId } })
      .useWatchData();

    return new Extension(data);
  }

  public static useTryLoadById(
    extensionId: string | undefined,
  ): Extension | undefined {
    const data = mittwaldApi.extensionGetExtension
      .getResource(extensionId ? { path: { extensionId } } : null)
      .useWatchData({ optional: true });

    return data ? new Extension(data) : undefined;
  }

  public static async loadById(extensionId: string): Promise<Extension> {
    const data = await mittwaldApi.extensionGetExtension
      .getResource({ path: { extensionId } })
      .load();

    return new Extension(data);
  }

  public useContributor(): Contributor {
    return Contributor.useLoadById(this.data.contributorId);
  }

  public async getContributor(): Promise<Contributor> {
    return Contributor.loadById(this.data.contributorId);
  }

  public getFrontendFragment(fragment: string): FrontendFragment | undefined {
    return this.frontendFragments[fragment];
  }

  public static async getCustomerIdFromContext(
    contextId: string,
    contextType: string,
  ): Promise<string> {
    if (!contextId) {
      throw new Error("No contextId provided");
    }

    if (contextType === "customer") {
      return contextId;
    } else if (contextType === "project") {
      const project = await mittwaldApi.projectGetProject.request({
        path: { projectId: contextId },
      });

      assertStatus(project, 200);

      if (!project.content.customerId) {
        throw new Error(`No customerId found for project ${contextId}`);
      }

      return project.content.customerId;
    }

    throw new Error(`Unknown context type: ${contextType}`);
  }

  public static async orderExtension(
    extensionId: string,
    consentedScopes: string[],
    context: { type: "project" | "customer"; id: string },
  ): Promise<void> {
    const requestBody = {
      consentedScopes: consentedScopes,
      ...(context.type === "customer"
        ? { customerId: context.id }
        : { projectId: context.id }),
    };

    await mittwaldApi.extensionOrderExtension
      .getResource({
        path: { extensionId },
        requestBody: requestBody as ExtensionCheckoutRequestBody,
      })
      .load();
  }

  public static async updateExistingExtensionInstanceContract(
    extensionInstanceId: string,
  ): Promise<void> {
    await mittwaldApi.extensionUpdateExtensionInstanceContract
      .getResource({
        path: { extensionInstanceId },
      })
      .load();
  }

  public hasFrontendFragment(fragment: string): boolean {
    return !!this.getFrontendFragment(fragment);
  }

  private normalizeFrontendFragments(
    fragments: FrontendFragments = {},
  ): FrontendFragments {
    return R.mapValues(fragments, (value) =>
      R.evolve(value, {
        additionalProperties: (val) =>
          R.mapValues(val, (v) => {
            if (typeof v === "string" && v.startsWith("{") && v.endsWith("}")) {
              return JSON.parse(v);
            }
            return v;
          }),
      }),
    );
  }
}

export default Extension;
